/** Nylas multipart: entire HTTP request (JSON message + body + signature + files) ≤ ~25 MB. */
export const NYLAS_MAX_SEND_BYTES = 25 * 1024 * 1024;

/** Reserve for message JSON, HTML body, signature, multipart boundaries. */
export const NYLAS_SEND_MESSAGE_OVERHEAD_BYTES = 1024 * 1024;

/** Budget for all attachment bytes combined (before subtracting actual message body on send). */
export const NYLAS_MAX_ATTACHMENTS_TOTAL_BYTES =
  NYLAS_MAX_SEND_BYTES - NYLAS_SEND_MESSAGE_OVERHEAD_BYTES;

export const NYLAS_MAX_ATTACHMENTS_COUNT = 5;

/** Rounded-down MB label for user-facing errors. */
export const NYLAS_MAX_ATTACHMENTS_TOTAL_MB = Math.floor(
  NYLAS_MAX_ATTACHMENTS_TOTAL_BYTES / (1024 * 1024),
);

import { buildEmailBodyWithSignature, utf8ByteLength } from "./emailBodyHtml.ts";

export type NylasAttachmentLike = { name?: string; size: number };

/** Byte length of the HTML body Nylas receives (same pipeline as sendNylasMessage). */
export function estimateNylasOutgoingBodyBytes(options: {
  text: string;
  html?: string;
  signatureHtml?: string | null;
  /** Outlook-style quote appended after signature when reply quoting is enabled. */
  quoteHtml?: string | null;
}): number {
  const html = String(options.html ?? "").trim();
  const text = String(options.text ?? "").trim();
  const bodyWithSignature = html
    ? buildEmailBodyWithSignature(html, options.signatureHtml, { isHtml: true })
    : buildEmailBodyWithSignature(text, options.signatureHtml);
  const quoteHtml = String(options.quoteHtml ?? "").trim();
  const body = quoteHtml ? `${bodyWithSignature}${quoteHtml}` : bodyWithSignature;
  return utf8ByteLength(body);
}

export function nylasAttachmentBudgetBytes(bodyTextBytes = 0): number {
  return Math.max(0, NYLAS_MAX_SEND_BYTES - NYLAS_SEND_MESSAGE_OVERHEAD_BYTES - bodyTextBytes);
}

export function validateNylasAttachments(
  files: NylasAttachmentLike[],
  options?: { bodyTextBytes?: number },
): string | null {
  if (files.length > NYLAS_MAX_ATTACHMENTS_COUNT) {
    return `Maximal ${NYLAS_MAX_ATTACHMENTS_COUNT} Anhänge.`;
  }
  const budget = nylasAttachmentBudgetBytes(options?.bodyTextBytes ?? 0);
  if (budget <= 0) {
    return "Nachrichtentext ist zu lang für Anhänge (Nylas-Limit 25 MB gesamt).";
  }
  const budgetMb = Math.max(1, Math.floor(budget / (1024 * 1024)));
  const tooBig = files.find((f) => f.size > budget);
  if (tooBig) {
    const label = tooBig.name?.trim() || "Datei";
    return `„${label}" ist zu groß (max. ca. ${budgetMb} MB für Anhänge inkl. Nachricht).`;
  }
  const total = files.reduce((sum, f) => sum + f.size, 0);
  if (total > budget) {
    return `Anhänge zusammen zu groß (max. ca. ${budgetMb} MB inkl. Nachricht, Nylas-Limit).`;
  }
  return null;
}

/** Merge picked files into an existing list and validate the result. */
export function mergeNylasAttachmentFiles(
  existing: File[],
  picked: File[],
  options?: { bodyTextBytes?: number },
): { files: File[]; error: string | null } {
  const merged = [...existing, ...picked];
  if (merged.length > NYLAS_MAX_ATTACHMENTS_COUNT) {
    return { files: existing, error: `Maximal ${NYLAS_MAX_ATTACHMENTS_COUNT} Anhänge.` };
  }
  const error = validateNylasAttachments(
    merged.map((f) => ({ name: f.name, size: f.size })),
    { bodyTextBytes: options?.bodyTextBytes },
  );
  if (error) return { files: existing, error };
  return { files: merged, error: null };
}
